import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { PublicLayout } from '@/components/layout/PublicLayout';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { KeyRound, Mail, ShieldCheck, MessageCircle } from 'lucide-react';

const ROLE_META = {
    user: { label: 'Account', loginPath: '/login' },
    reseller: { label: 'Reseller', loginPath: '/reseller/login' },
    admin: { label: 'Admin', loginPath: '/admin/login' },
};

export default function ForgotPasswordPage() {
    const [params] = useSearchParams();
    const role = ['user', 'reseller', 'admin'].includes(params.get('role')) ? params.get('role') : 'user';
    const meta = ROLE_META[role];
    const navigate = useNavigate();

    const [step, setStep] = useState('email'); // 'email' | 'reset'
    const [email, setEmail] = useState('');
    const [channel, setChannel] = useState('email'); // 'email' | 'whatsapp' (users only)
    const [mobile, setMobile] = useState('');
    const isWa = role === 'user' && channel === 'whatsapp';
    const ident = () => (isWa ? { channel: 'whatsapp', mobile: mobile.replace(/\D/g, '').slice(-10) } : { channel: 'email', email: email.trim().toLowerCase() });
    const [code, setCode] = useState('');
    const [newPwd, setNewPwd] = useState('');
    const [showPwd, setShowPwd] = useState(false);
    const [loading, setLoading] = useState(false);

    const sendCode = async (e) => {
        e && e.preventDefault();
        if (isWa ? mobile.replace(/\D/g, '').length < 10 : !email.trim()) { toast.error(isWa ? 'Enter your registered 10-digit WhatsApp number' : 'Enter your email'); return; }
        setLoading(true);
        try {
            const { data } = await api.post('/auth/forgot-password', { ...ident(), role });
            toast.success(data.message || 'If an account exists, a code has been sent.');
            if (data.dev_otp) toast.message(`DEV OTP: ${data.dev_otp}`);
            setStep('reset');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Could not send reset code');
        } finally { setLoading(false); }
    };

    const doReset = async (e) => {
        e && e.preventDefault();
        if (code.trim().length < 4) { toast.error(`Enter the 6-digit code from your ${isWa ? 'WhatsApp' : 'email'}`); return; }
        if (newPwd.length < 6) { toast.error('Password must be at least 6 characters'); return; }
        setLoading(true);
        try {
            await api.post('/auth/reset-password', { ...ident(), role, code: code.trim(), new_password: newPwd });
            toast.success('Password updated! Please log in.');
            navigate(meta.loginPath);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Reset failed');
        } finally { setLoading(false); }
    };

    return (
        <PublicLayout>
            <div className="min-h-[70vh] flex items-center justify-center px-4 py-10">
                <Card className="max-w-md w-full p-6 sm:p-8 shadow-card" data-testid="forgot-password-card">
                    <div className="flex items-center gap-2 mb-1">
                        <div className="h-10 w-10 rounded-lg bg-[#0E2A47] text-white flex items-center justify-center"><KeyRound className="h-5 w-5" /></div>
                        <div>
                            <div className="font-display font-semibold text-lg">Reset {meta.label} password</div>
                            <div className="text-xs text-muted-foreground">Verify via {role === 'user' ? 'WhatsApp or email' : 'email'} OTP</div>
                        </div>
                    </div>
                    <div className="mt-4 flex items-center gap-2 text-[12px] text-muted-foreground">
                        <ShieldCheck className="h-3.5 w-3.5" /> We'll send a 6-digit code {isWa ? 'on WhatsApp' : 'to your email'} to confirm it's you.
                    </div>

                    {step === 'email' ? (
                        <form onSubmit={sendCode} className="mt-6 space-y-4">
                            {role === 'user' && (
                                <div className="grid grid-cols-2 gap-1 rounded-full border border-border p-1 bg-secondary/50" data-testid="forgot-channel-toggle">
                                    {[['email', 'Email', Mail], ['whatsapp', 'WhatsApp', MessageCircle]].map(([k, label, Icon]) => (
                                        <button key={k} type="button" onClick={() => setChannel(k)}
                                            className={`flex items-center justify-center gap-1.5 rounded-full py-1.5 text-sm font-medium transition-colors ${channel === k ? 'bg-[#0E2A47] text-white' : 'text-muted-foreground hover:text-foreground'}`}
                                            data-testid={`forgot-channel-${k}`}><Icon className="h-4 w-4" /> {label}</button>
                                    ))}
                                </div>
                            )}
                            {isWa ? (
                                <div className="space-y-2">
                                    <Label htmlFor="fp-mobile">Registered WhatsApp number</Label>
                                    <Input id="fp-mobile" data-testid="forgot-mobile-input" inputMode="numeric" maxLength={13} placeholder="10-digit mobile number" value={mobile} onChange={(e) => setMobile(e.target.value.replace(/[^\d+]/g, ''))} autoFocus />
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    <Label htmlFor="fp-email">Email</Label>
                                    <Input id="fp-email" data-testid="forgot-email-input" type="email" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} autoFocus />
                                </div>
                            )}
                            <Button type="submit" disabled={loading} className="w-full bg-[#0E2A47] hover:bg-[#0B223A]" data-testid="forgot-send-code-button">
                                {isWa ? <MessageCircle className="h-4 w-4 mr-2" /> : <Mail className="h-4 w-4 mr-2" />} {loading ? 'Sending…' : `Send reset code${isWa ? ' on WhatsApp' : ''}`}
                            </Button>
                        </form>
                    ) : (
                        <form onSubmit={doReset} className="mt-6 space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="fp-code">{isWa ? 'WhatsApp' : 'Email'} OTP</Label>
                                <Input id="fp-code" data-testid="forgot-otp-input" inputMode="numeric" maxLength={6} placeholder="6-digit code" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} autoFocus />
                            </div>
                            <div className="space-y-2">
                                <div className="flex items-center justify-between"><Label htmlFor="fp-newpwd">New password</Label>
                                    <button type="button" onClick={() => setShowPwd(v => !v)} className="text-xs text-[#1F9D8A] hover:underline" data-testid="forgot-toggle-password">{showPwd ? 'Hide' : 'Show'}</button></div>
                                <Input id="fp-newpwd" data-testid="forgot-new-password-input" type={showPwd ? 'text' : 'password'} placeholder="Min 6 characters" value={newPwd} onChange={(e) => setNewPwd(e.target.value)} />
                            </div>
                            <Button type="submit" disabled={loading} className="w-full bg-[#0E2A47] hover:bg-[#0B223A]" data-testid="forgot-reset-button">
                                {loading ? 'Updating…' : 'Update password'}
                            </Button>
                            <button type="button" onClick={() => setStep('email')} className="w-full text-xs text-muted-foreground hover:text-foreground" data-testid="forgot-back-button">
                                {isWa ? 'Use a different number' : 'Use a different email'}
                            </button>
                        </form>
                    )}

                    <div className="mt-5 text-center text-sm text-muted-foreground">
                        <Link to={meta.loginPath} className="text-[#1F9D8A] font-medium hover:underline" data-testid="forgot-back-to-login">Back to login</Link>
                    </div>
                </Card>
            </div>
        </PublicLayout>
    );
}
